/**
 * Scoped MUI theme for the onboarding surfaces: the first-time wizard in
 * Redirect.tsx and the returning-member "Welcome back" prompts.
 *
 * Why a scoped theme instead of per-component `sx`: the app theme in
 * src/utils/theme.ts is light by default (white primary, #EFF0F4 input
 * borders, dark-on-light text) while the platform itself runs dark. Dialogs
 * that went dark before had to fight that theme field by field with
 * `!important` overrides, and anything rendered through a portal (menus,
 * autocomplete papers, date pickers) still came out light. Wrapping the
 * onboarding dialogs in a ThemeProvider fixes all of that at once: React
 * context follows the component tree, not the DOM, so portaled papers pick
 * up the dark palette too.
 *
 * Palette: the Dosen 3.0 tokens already used across the dark UI
 * (src/components/ui/theme/tokens.ts). Layout, progress language and copy
 * follow Ronan's onboarding design study; the study's navy ground was
 * swapped for the app's neutral dark so onboarding matches the product.
 */
import { createTheme } from '@mui/material/styles';
import type {} from '@mui/lab/themeAugmentation';
import { theme as baseTheme } from '../../../utils/theme';
import { colors as tokens } from '../../../components/ui/theme/tokens';

export const onboardingColors = {
  /** Page ground behind the card. */
  page: '#0F0F0F',
  /** The card itself. */
  card: tokens.card.fillDefault, // #151515
  cardBorder: tokens.stroke.borderDim, // #2A2A2A
  /** Inputs, chips, option rows. */
  surface: '#1C1C1C',
  surfaceHover: '#262626',
  surfaceSelected: '#2A2A2A',
  border: tokens.stroke.borderDim, // #2A2A2A
  borderStrong: tokens.stroke.divider, // #444444
  ink: tokens.text.primary, // #FDFDFD
  inkSecondary: tokens.text.secondary, // #B6B6B6
  inkTertiary: tokens.text.tertiary, // #868686
  placeholder: tokens.text.tertiary,
  track: tokens.stroke.borderDim,
  accent: tokens.brand.orange500, // #F79420
  accentDeep: tokens.brand.orange700, // #F15A26
  accentTint: 'rgba(247,148,32,0.12)',
  accentRing: 'rgba(247,148,32,0.28)',
  accentGlow: 'rgba(247,148,32,0.45)',
  success: '#2FB483',
  error: '#FF6B6B',
  gradient: `linear-gradient(90deg, ${tokens.brand.orange500} 0%, ${tokens.brand.orange700} 100%)`,
  gradientButton: `linear-gradient(180deg, ${tokens.brand.orange500} 0%, ${tokens.brand.orange700} 100%)`,
  gradientButtonHover: 'linear-gradient(180deg, #E08414 0%, #D94F1E 100%)'
} as const;

const C = onboardingColors;

/** Inline-styled markup the theme cannot reach (global SCSS classes). Applied
 *  to the dialog paper by OnboardingDialog so it scopes to onboarding only. */
export const onboardingSurfaceSx = {
  // Selectable topic / industry chips (.CheckBtnChip in custom.scss is light).
  '& .CheckBtnChip': {
    border: `1px solid ${C.border}`,
    backgroundColor: C.surface,
    borderRadius: 999,
    padding: '10px 16px',
    transition: 'border-color .15s, background-color .15s',
    '& svg': { color: C.inkTertiary, fontSize: 18 },
    '& p': { color: C.ink, fontWeight: 500 },
    '&:hover': { borderColor: C.borderStrong }
  },
  '& .CheckBtnChip.checked': {
    backgroundColor: C.accentTint,
    border: `1px solid ${C.accent}`,
    '& svg': { color: C.accent },
    '& p': { color: C.ink }
  },
  // Assessment yes/no rows (.industries-sec in menteeTask.scss is light).
  '& .industries-sec': {
    border: `1px solid ${C.border}`,
    backgroundColor: C.surface,
    borderRadius: 10,
    height: 'auto',
    minHeight: 50
  },
  // Profile photo square (.profileImageBox in custom.scss is light).
  '& .profileImageBox': { background: C.surface, border: `1px solid ${C.border}` },
  // Shared modal header used by the auxiliary editors.
  '& .chat-dialog-header': { background: 'transparent', borderBottom: `1px solid ${C.border}` },
  '& .chat-dialog-title': { color: `${C.ink} !important` },
  // custom.scss forces every dialog-action button to 50px; the card uses 46.
  '& .MuiDialogActions-root button': { height: 46 }
} as const;

/** The card: what every onboarding dialog paper looks like. */
export const onboardingPaperSx = {
  width: '100%',
  maxWidth: 560,
  margin: 2,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: C.card,
  backgroundImage: 'none',
  color: C.ink,
  border: `1px solid ${C.cardBorder}`,
  borderRadius: '16px',
  boxShadow: '0 30px 80px -30px rgba(0,0,0,0.85)',
  '&.MuiDialog-paperFullScreen': {
    margin: 0,
    maxWidth: '100%',
    borderRadius: 0,
    border: 'none'
  }
} as const;

/** Page ground behind the card, with a faint brand glow top-right. */
export const onboardingBackdropSx = {
  backgroundColor: C.page,
  backgroundImage: 'radial-gradient(900px 520px at 82% -10%, rgba(247,148,32,0.07), transparent 60%)'
} as const;

const heading = { color: C.ink };

export const onboardingTheme = createTheme(baseTheme, {
  palette: {
    mode: 'dark',
    background: { default: C.page, paper: C.card },
    text: { primary: C.ink, secondary: C.inkSecondary, disabled: C.inkTertiary },
    divider: C.border,
    action: {
      active: C.inkSecondary,
      hover: 'rgba(255,255,255,0.06)',
      selected: 'rgba(255,255,255,0.10)',
      disabled: C.inkTertiary,
      disabledBackground: 'rgba(255,255,255,0.08)',
      focus: 'rgba(255,255,255,0.12)'
    },
    primary: { main: C.accent, dark: C.accentDeep, light: '#FBB871', contrastText: '#FFFFFF' },
    secondary: { main: C.inkSecondary, light: C.inkTertiary, contrastText: C.page },
    error: { main: C.error },
    success: { main: C.success }
  },
  typography: {
    h1: heading,
    h2: heading,
    h3: heading,
    h4: heading,
    h5: heading,
    h6: heading,
    subtitle1: { color: C.inkSecondary },
    subtitle2: { color: C.ink },
    body1: { color: C.inkSecondary },
    body2: { color: C.inkSecondary },
    caption: { color: C.inkTertiary }
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: { backgroundColor: C.card, backgroundImage: 'none', color: C.ink }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: { backgroundColor: C.card, backgroundImage: 'none', color: C.ink }
      }
    },
    MuiDialogTitle: { styleOverrides: { root: { color: C.ink } } },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          color: C.ink,
          // Classic (non-overlay) scrollbar: macOS overlay bars stay invisible
          // until scrolled, which once hid a required field below the fold.
          scrollbarWidth: 'thin',
          scrollbarColor: `${C.borderStrong} transparent`,
          '&::-webkit-scrollbar': { width: 8 },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: C.borderStrong,
            borderRadius: 4,
            border: '2px solid transparent',
            backgroundClip: 'content-box'
          },
          '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' }
        }
      }
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '8px 24px 24px',
          gap: 12,
          '& > :not(:first-of-type)': { marginLeft: 0 }
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          minHeight: 46,
          borderRadius: 10,
          fontWeight: 600,
          fontSize: 14,
          textTransform: 'none',
          boxShadow: 'none'
        },
        contained: {
          background: C.gradientButton,
          color: C.ink,
          border: 'none',
          boxShadow: '0 6px 18px -6px rgba(242,101,34,0.6)',
          '&:hover': { background: C.gradientButtonHover, boxShadow: '0 6px 18px -6px rgba(242,101,34,0.75)' },
          // House disabled: dark with dim text, lights up orange once available.
          '&.Mui-disabled': {
            background: 'rgba(0, 0, 0, 0.2)',
            color: '#565656',
            boxShadow: `inset 0 0 0 1px ${C.border}`,
            opacity: 1
          }
        },
        // House secondary: dark fill, white text, hairline border.
        outlined: {
          borderColor: C.border,
          color: C.ink,
          backgroundColor: C.surface,
          '&:hover': { borderColor: C.border, backgroundColor: C.surfaceHover },
          '&.Mui-disabled': { borderColor: C.border, color: '#565656', backgroundColor: 'rgba(0, 0, 0, 0.2)' }
        },
        text: { color: C.inkSecondary, '&:hover': { color: C.ink, backgroundColor: 'rgba(255,255,255,0.04)' } }
      }
    },
    MuiLoadingButton: {
      styleOverrides: {
        root: {
          '& .MuiLoadingButton-loadingIndicator': { color: '#FFFFFF' },
          '&.MuiButton-outlined .MuiLoadingButton-loadingIndicator': { color: C.ink }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: C.surface,
          borderRadius: 10,
          color: C.ink,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: C.borderStrong },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: `1px solid ${C.accent}`,
            boxShadow: `0 0 0 3px ${C.accentRing}`
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: C.error },
          '&.Mui-disabled': { backgroundColor: C.page, borderColor: `${C.border} !important` },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: `${C.border} !important` }
        },
        notchedOutline: { borderColor: C.border },
        input: {
          color: C.ink,
          '&::placeholder': { color: C.placeholder, opacity: 1 },
          '&.Mui-disabled': { WebkitTextFillColor: C.inkTertiary, color: C.inkTertiary }
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          color: C.ink,
          '& .Mui-disabled': { background: `${C.page} !important`, borderRadius: '10px !important' }
        },
        input: { '&::placeholder': { color: C.placeholder, opacity: 1 } }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: `${C.inkSecondary} !important`,
          fontSize: '13px !important',
          fontWeight: '600 !important',
          marginBottom: '6px !important'
        }
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: `${C.inkSecondary} !important`,
          fontSize: '13px !important',
          fontWeight: '600 !important',
          marginBottom: '6px !important'
        }
      }
    },
    MuiFormControlLabel: { styleOverrides: { label: { color: C.ink } } },
    MuiFormHelperText: {
      styleOverrides: { root: { color: C.inkTertiary, '&.Mui-error': { color: C.error } } }
    },
    MuiInputAdornment: { styleOverrides: { root: { color: C.inkSecondary } } },
    MuiAutocomplete: {
      styleOverrides: {
        root: { '& .Mui-disabled': { background: `${C.page} !important` } },
        paper: {
          backgroundColor: C.surface,
          color: C.ink,
          border: `1px solid ${C.border}`,
          boxShadow: '0 12px 32px -12px rgba(0,0,0,0.8)'
        },
        listbox: { padding: 4 },
        option: {
          color: `${C.ink} !important`,
          borderRadius: 6,
          '&.Mui-focused': { backgroundColor: `${C.surfaceHover} !important` },
          '&[aria-selected="true"]': { backgroundColor: `${C.surfaceSelected} !important` }
        },
        noOptions: { color: C.inkTertiary },
        loading: { color: C.inkTertiary },
        tag: {
          backgroundColor: 'rgba(255,255,255,0.12)',
          color: `${C.ink} !important`,
          border: 'none',
          '& .MuiChip-deleteIcon': { color: C.inkSecondary, '&:hover': { color: C.ink } }
        },
        popupIndicator: { color: C.inkSecondary },
        clearIndicator: { color: C.inkSecondary }
      }
    },
    MuiSelect: {
      styleOverrides: {
        select: { color: C.ink, '& .Mui-disabled': { background: `${C.page} !important` } },
        icon: { color: C.inkSecondary }
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: { backgroundColor: `${C.surface} !important`, color: C.ink, border: `1px solid ${C.border}` }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: `${C.ink} !important`,
          '&:hover': { backgroundColor: C.surfaceHover },
          '&.Mui-selected': { backgroundColor: `${C.surfaceSelected} !important` },
          '&.Mui-disabled': { color: `${C.inkTertiary} !important`, opacity: 1 }
        }
      }
    },
    MuiPopover: {
      styleOverrides: {
        paper: { backgroundColor: C.surface, color: C.ink, border: `1px solid ${C.border}` }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: { backgroundColor: 'rgba(255,255,255,0.12)', color: C.ink },
        deleteIcon: { color: C.inkSecondary, '&:hover': { color: C.ink } },
        outlined: { borderColor: C.borderStrong }
      }
    },
    MuiCheckbox: {
      styleOverrides: { root: { color: '#565656', '&.Mui-checked': { color: C.accent } } }
    },
    MuiRadio: {
      styleOverrides: { root: { color: '#565656', '&.Mui-checked': { color: C.accent } } }
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { height: 6, borderRadius: 3, backgroundColor: C.track },
        bar: { borderRadius: 3, background: C.gradient, boxShadow: `0 0 12px ${C.accentGlow}` }
      }
    },
    MuiDivider: { styleOverrides: { root: { borderColor: C.border } } },
    MuiIconButton: { styleOverrides: { root: { color: C.inkSecondary } } },
    MuiAvatar: { styleOverrides: { root: { backgroundColor: C.surface, color: C.inkSecondary } } },
    MuiTooltip: {
      styleOverrides: { tooltip: { backgroundColor: C.surfaceSelected, color: C.ink } }
    }
  }
});
